import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ExportModal = ({ open, onClose, currentTool }) => {
  const [selectedFormat, setSelectedFormat] = useState(null);
  const allExportFormats = [
    // 2D formats
    {
      id: "pgm",
      name: "PGM",
      title: "Image / Map",
      description: "Portable GrayMap",
      extension: ".pgm",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "2D",
    },
    {
      id: "stcm",
      name: "STCM",
      title: "Mapping / Robotics",
      description: "Spatial / Scan Tracking Cloud Map",
      extension: ".stcm",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "2D",
    },
    {
      id: "yaml",
      name: "YAML",
      title: "Configuration",
      description: "YAML Map Configuration",
      extension: ".yaml",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "2D",
    },
    {
      id: "json",
      name: "JSON",
      title: "Data Format",
      description: "JavaScript Object Notation",
      extension: ".json",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "2D",
    },
    // 3D formats
    {
      id: "e57",
      name: "E57",
      title: "Point Cloud",
      description: "ASTM E57 3D Imaging Data File",
      extension: ".e57",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "3D",
    },
    {
      id: "ply",
      name: "PLY",
      title: "Point Cloud / Mesh",
      description: "Polygon File Format",
      extension: ".ply",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "3D",
    },
    {
      id: "pcd",
      name: "PCD",
      title: "Point Cloud",
      description: "Point Cloud Data (PCL)",
      extension: ".pcd",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "3D",
    },
    {
      id: "ifc",
      name: "IFC",
      title: "BIM / CAD",
      description: "Industry Foundation Classes",
      extension: ".ifc",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "3D",
    },
    // 360 formats
    {
      id: "360images",
      name: "360 Images",
      title: "Panoramic Images",
      description: "360 Degree Panoramic Photos",
      extension: ".jpg",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "360Images",
    },
    {
      id: "obj",
      name: "OBJ",
      title: "3D Model",
      description: "Wavefront Object File",
      extension: ".obj",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "360Images",
    },
    {
      id: "gltf",
      name: "GLTF",
      title: "3D Model",
      description: "GL Transmission Format",
      extension: ".gltf",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "360Images",
    },
    {
      id: "glb",
      name: "GLB",
      title: "3D Model",
      description: "Binary GL Transmission Format",
      extension: ".glb",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />,
      category: "360Images",
    },
  ];

  // Filter formats based on current tool
  const exportFormats = allExportFormats.filter(
    (format) => format.category === currentTool
  );

  const handleGenerateLink = () => {
    // Handle generate integration link
    console.log("Generate integration link");
  };

  const handleExport = () => {
    if (!selectedFormat) {
      alert("Please select a format to export");
      return;
    }

    // Create dummy file content
    const dummyContent = `This is a dummy ${selectedFormat.name} file\nGenerated on: ${new Date().toISOString()}\nFormat: ${selectedFormat.extension}`;
    
    // Create blob and download
    const blob = new Blob([dummyContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `export_${currentTool}_${Date.now()}${selectedFormat.extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log("Export:", selectedFormat.name);
    onClose();
  };

  const handleFormatClick = (format) => {
    setSelectedFormat(format);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#112138",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <FileDownloadOutlinedIcon />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Export Floor Plan
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Choose your preferred format
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "white",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 2,
            mb: 3,
            mt: 3
          }}
        >
          {exportFormats.map((format) => (
            <Box
              key={format.id}
              onClick={() => handleFormatClick(format)}
              sx={{
                border: selectedFormat?.id === format.id 
                  ? "2px solid #112138" 
                  : "1.5px solid #e0e0e0",
                borderRadius: 2,
                p: 2,
                cursor: "pointer",
                transition: "all 0.2s ease",
                bgcolor: selectedFormat?.id === format.id 
                  ? "rgba(17, 33, 56, 0.04)" 
                  : "transparent",
                position: "relative",
                "&:hover": {
                  borderColor: "#112138",
                  boxShadow: "0 2px 8px rgba(17, 33, 56, 0.1)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {selectedFormat?.id === format.id && (
                <CheckCircleIcon
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "#112138",
                    fontSize: 20,
                  }}
                />
              )}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    bgcolor: "#e3f2fd",
                    color: "#112138",
                    borderRadius: 1.5,
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {format.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={600}>
                    {format.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    {format.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ mt: 0.5, fontSize: "0.7rem" }}
                  >
                    {format.description}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1,
                      display: "inline-block",
                      color: "#112138",
                      fontWeight: 500,
                    }}
                  >
                    {format.extension}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
    
          <Button
            variant="contained"
            onClick={handleExport}
            sx={{
              bgcolor: "#112138",
              color: "white",
              textTransform: "none",
              px: 4,
              py: 1,
              borderRadius: 2,
              "&:hover": {
                bgcolor: "#0d1a2d",
              },
            }}
          >
            Export
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
